// Watch-schedule presets — the plain-language answer to "when should this alarm
// watch?", shared by the recipe form and the custom builder.
//
// Both authoring surfaces used to ask the question as a row of six raw mode
// checkboxes (Underway / Anchored / Moored / Docked / Maintenance / Sailing),
// which is the monitor's INTERNAL vocabulary, pre-ticked in a combination the
// operator then had to reverse-engineer. Nobody thinks about their boat in six
// modes; they think "always", "only when we're going somewhere", "only when
// we're stopped". The presets are those three sentences, with the grid kept
// behind "Custom…" for the person who genuinely wants Maintenance-only.
//
// WHY SAILING RIDES ALONG WITH UNDERWAY ON WRITE. The mode detector can elect
// `Sailing`, and an "always" alarm written without it would be DISARMED for as
// long as the boat is sailing — a safety regression, and an invisible one. So
// every preset that covers being underway writes Sailing too.
//
// The compiler now folds Sailing onto Underway as well (core ADR-109 / cloud
// ADR 0175, `COMPILER_OUTPUT_VERSION` 5), which makes the write redundant on a
// boat running that compiler. THE WRITES STAY ANYWAY, and so do the gap notes
// below: a boat is only covered once it has taken the core release carrying the
// fold and rebooted, and until then the mode set stored here is the only thing
// keeping its alarms armed under sail. Retiring the Sailing writes and the gap
// notes is a follow-up triggered by fleet core adoption, tracked in
// `cloud/tasks/alarm_sailing_fold.md` — not something to tidy up early.
//
// What DID change is the Custom… grid: it no longer offers Sailing as a
// checkbox (see {@link watchGridModes}), because on an updated boat ticking or
// unticking it decides nothing. The grid WRITES it the same way the presets do
// instead — ticking Underway adds Sailing, unticking Underway removes it (see
// {@link toggleWatchGridMode}) — so a hidden checkbox never becomes a mode the
// grid cannot un-write, and a custom set keeps its sailing coverage through the
// skew window.
//
// DETECTION is the inverse and is deliberately LOSSY-TOLERANT rather than
// exact: a stored definition's modes are canonicalized (case-folded onto the
// runtime vocabulary, then Sailing folded into Underway) before being compared
// set-wise against each preset. That way an alarm authored before Sailing was
// reachable in these editors — `[Underway]`, or all five non-Sailing modes —
// still reads as "Only underway" / "Always" instead of dumping the operator
// into the grid. What detection must NEVER do is rewrite the draft: reading a
// def as "Only underway" does not add Sailing to it. Opening a CANONICAL-SPELLED
// def and saving it untouched has to produce the exact same `enabledModes`, or
// merely looking at an alarm re-pushes the boat's ruleset. (A legacy
// lowercase-moded def is the one exception, and it is not this module's doing:
// `initFromDef` canonicalizes the spelling, so its untouched save carries a
// case-normalizing `enabledModes` patch. That fold predates the presets and is
// wanted — the write APIs take the canonical enum.)
//
// The cost of tolerant detection is a claim the stored set does not back: an
// older def reads as "Always" while being disarmed under sail. Detection still
// must not rewrite it, so the field DISCLOSES the difference instead — see
// {@link watchPresetGap} and {@link watchGapNote}.

// WHAT THE VESSEL TYPE CHANGES HERE (ADR 0173 / core ADR-110). "Sailing shouldn't
// show up as a mode for a power boat." A boat that has told us it is a POWER boat
// gets two things dropped: the "or sail" half of the underway preset's hint, and
// the sailing gap disclosure — because a filtered mode detector never elects
// Sailing on that boat, so the gap it warns about cannot open.
//
// It is gated on an AFFIRMATIVE `'power'` and nothing else. Unknown — which is
// most of the fleet, and every boat nobody has asked yet — keeps today's
// cautious wording and today's disclosure, because "we have not asked" is not
// evidence the boat has no sails. A sailboat keeps everything too.
//
// What does NOT change is the WRITE: every preset still writes `Sailing`
// alongside `Underway` on a power boat exactly as before. The ridealong is what
// protects a boat still running a pre-fold compiler, and it costs a power boat
// nothing — a mode its detector will never elect.

import type { VesselType } from '@neomaris/protocol';
import { CANONICAL_MODES, foldModesToCanonical } from '@neomaris/rules';

/** The runtime mode that only ever rides along; see the Sailing note above. */
const SAILING = 'Sailing';
/** The mode `Sailing` folds onto for DETECTION (never for the write). */
const UNDERWAY = 'Underway';
/** The modes that mean "the boat is stopped". */
const AT_REST: readonly string[] = ['Anchored', 'Moored', 'Docked'];

/** The four answers the field offers. `custom` is the escape hatch, not a set. */
export type WatchPresetId = 'always' | 'underway' | 'rest' | 'custom';

/** One preset: the words, and the modes choosing it writes. */
export interface WatchPreset {
  readonly id: Exclude<WatchPresetId, 'custom'>;
  /** The radio label. */
  readonly label: string;
  /**
   * The "which modes is that" parenthetical beside the label, rendered the way
   * every other hint on these forms is (`Low voltage (8–60 V)`).
   */
  readonly hint: string;
  /** The modes this preset WRITES, in canonical-universe order. */
  readonly modes: readonly string[];
}

/**
 * The presets, in offer order. Every mode list is derived from
 * {@link CANONICAL_MODES} rather than spelled out, so a mode added to the
 * shared vocabulary lands in "Always" instead of quietly being unreachable
 * except through the grid.
 */
export const WATCH_PRESETS: readonly WatchPreset[] = [
  {
    id: 'always',
    label: 'Always',
    hint: 'whatever the boat is doing',
    modes: [...CANONICAL_MODES],
  },
  {
    id: 'underway',
    label: 'Only underway',
    hint: 'under power or sail',
    modes: CANONICAL_MODES.filter((m) => m === UNDERWAY || m === SAILING),
  },
  {
    id: 'rest',
    label: 'Only at rest',
    hint: 'at anchor, on a mooring, or at the dock',
    modes: CANONICAL_MODES.filter((m) => AT_REST.includes(m)),
  },
];

/**
 * The underway preset's hint on a boat that has told us it has no sails. Same
 * modes, same write — only the words change, because "under power or sail" is
 * a distinction that does not exist on a power boat and reads like the product
 * does not know whose boat it is looking at.
 */
const UNDERWAY_HINT_POWER = 'under way';

/**
 * The presets as THIS boat should read them.
 *
 * The only vessel-type-sensitive part is the underway hint. Callers that render
 * preset words — the authoring radio, and the row note that borrows a preset's
 * hint mid-sentence — must go through here rather than reading
 * {@link WATCH_PRESETS} directly, or one surface says "under power or sail"
 * while the one below it says "under way" about the same alarm.
 */
export function watchPresets(vesselType?: VesselType | null): readonly WatchPreset[] {
  if (vesselType !== 'power') return WATCH_PRESETS;
  return WATCH_PRESETS.map((preset) =>
    preset.id === 'underway' ? { ...preset, hint: UNDERWAY_HINT_POWER } : preset
  );
}

/** The "let me pick the modes myself" radio label, and its hint. */
export const CUSTOM_WATCH_LABEL = 'Custom…';
export const CUSTOM_WATCH_HINT = 'pick the modes yourself';

/**
 * The modes the Custom… grid OFFERS as checkboxes — the caller's universe with
 * `Sailing` removed.
 *
 * Post-fold (core ADR-109 / cloud ADR 0175) the compiler appends `Sailing` to
 * any rule enabled `Underway`, so on an updated boat that checkbox decides
 * nothing: ticked or unticked, an underway alarm watches while sailing. A
 * control that cannot change the outcome is worse than no control — it invites
 * an owner to "fix" a sailing gap by ticking a box that was never the cause,
 * and it invites the opposite mistake of unticking it and believing the alarm
 * is now off under sail.
 *
 * Removed only from the GRID. The presets still WRITE Sailing (see the header
 * note), stored sets still carry it, and every read surface still renders the
 * chip verbatim — a def that says Sailing shows Sailing. This function decides
 * what is offered, never what is stored: `enabledModes` is rendered and written
 * from the draft, so a hidden mode is preserved untouched rather than dropped.
 */
export function watchGridModes(modeUniverse: readonly string[]): string[] {
  return modeUniverse.filter((m) => m !== SAILING);
}

/**
 * Apply one Custom… grid tick to a mode set — and carry `Sailing` with
 * `Underway`, exactly as the presets do.
 *
 * THE GRID MUST BE ABLE TO UN-WRITE ANYTHING A PRESET WRITES. Hiding the
 * `Sailing` checkbox ({@link watchGridModes}) without this broke that invariant
 * three ways, all of them silent:
 *
 * 1. A preset writes `Sailing`; switching to Custom… and unticking `Underway`
 *    left `Sailing` behind, stored and unremovable through this editor.
 * 2. A set composed IN the grid could never gain `Sailing` at all — so during
 *    the rollout skew window (a boat still on a pre-fold compiler, where the
 *    stored set is the ONLY thing keeping an alarm armed under sail) the grid
 *    quietly authored the very gap the presets exist to close.
 * 3. The gap note would then tell the owner to "pick Always or Only underway
 *    again" — advice that works, about a hole the editor had just dug.
 *
 * So `Underway` on ⇒ `Sailing` on, `Underway` off ⇒ `Sailing` off, and no other
 * mode touches it. A legacy def carrying `Sailing` WITHOUT `Underway` keeps it
 * through every other tick (and still renders its chip verbatim); it loses it
 * only if `Underway` is ticked and then unticked, which is the operator saying
 * "not under way" with the whole underway family in scope.
 *
 * Returns the raw set — the caller re-orders it against its own mode universe,
 * which is also what drops `Sailing` if a caller's universe lacks it.
 */
export function toggleWatchGridMode(current: readonly string[], mode: string): Set<string> {
  const next = new Set(current);
  const turningOn = !next.has(mode);
  if (turningOn) next.add(mode);
  else next.delete(mode);
  if (mode === UNDERWAY) {
    if (turningOn) next.add(SAILING);
    else next.delete(SAILING);
  }
  return next;
}

/** The modes a preset writes. `custom` writes nothing — it reveals the grid. */
export function watchPresetModes(id: WatchPresetId): string[] {
  const preset = WATCH_PRESETS.find((p) => p.id === id);
  return preset === undefined ? [] : [...preset.modes];
}

/**
 * A stable comparison key for a mode set: case-folded onto the canonical
 * vocabulary (so a legacy lowercase row still matches), with `Sailing` folded
 * into `Underway` (so an alarm written before Sailing rode along still reads as
 * the preset it means), deduped and sorted (so order never decides).
 *
 * Unknown modes are dropped by the fold — an alarm carrying one is not a
 * preset, and dropping it here only affects the RADIO; the grid still renders
 * `enabledModes` verbatim.
 */
export function watchModeKey(modes: readonly string[]): string {
  return [...canonicalWatchModes(modes)].sort().join('|');
}

/**
 * A mode set folded onto the DETECTION universe: case-folded onto the canonical
 * runtime vocabulary, `Sailing` folded into `Underway`, deduped, first-seen
 * order kept. THE one fold — {@link watchModeKey} sorts it into a comparison
 * key, and the read surfaces ask it "is the boat's current mode in this set?"
 * (`alarmDisplay.alarmPausedNote`). Two folds would let a row claim an alarm is
 * paused while the radio above it says the alarm is always on.
 */
export function canonicalWatchModes(modes: readonly string[]): string[] {
  const out: string[] = [];
  for (const mode of foldModesToCanonical([...modes])) {
    const folded = mode === SAILING ? UNDERWAY : mode;
    if (!out.includes(folded)) out.push(folded);
  }
  return out;
}

/**
 * Which preset a stored mode set reads as, or `'custom'` when it matches none
 * (including the empty set — an alarm with no modes is not "always").
 */
export function detectWatchPreset(enabledModes: readonly string[]): WatchPresetId {
  const key = watchModeKey(enabledModes);
  if (key.length === 0) return 'custom';
  return WATCH_PRESETS.find((p) => watchModeKey(p.modes) === key)?.id ?? 'custom';
}

// --- disclosing what tolerant detection glosses over -----------------------

/**
 * The canonical modes the DETECTED preset writes that this set does not carry —
 * empty when the set matches no preset (nothing is being claimed about it) or
 * already carries the whole preset.
 *
 * This is the honest edge of the lossy fold above. `[Underway, Anchored,
 * Moored, Docked, Maintenance]` READS as "Always", and re-writing it on sight
 * is exactly what the round-trip invariant forbids — so the radio says "Always"
 * over a def that is disarmed for the whole passage. The gap is that difference,
 * stated rather than hidden.
 *
 * Only ever `['Sailing']` (the common case: an alarm authored before Sailing was
 * reachable) or `['Underway']` (an unusual Sailing-only def, which folds onto
 * "Only underway"). Every other preset match is exact by construction.
 *
 * ON A DECLARED POWER BOAT the `Sailing` half is dropped (ADR 0173): its mode
 * detector never elects Sailing, so a set missing that mode is missing nothing
 * that can ever happen, and warning about it is noise on an owner who already
 * told us what kind of boat they have. `['Underway']` still surfaces — a
 * Sailing-only def on a power boat genuinely watches nothing. Unknown and
 * `'sail'` keep the full disclosure.
 */
export function watchPresetGap(modes: readonly string[], vesselType?: VesselType | null): string[] {
  const id = detectWatchPreset(modes);
  if (id === 'custom') return [];
  const have = new Set(foldModesToCanonical([...modes]));
  const gap = watchPresetModes(id).filter((m) => !have.has(m));
  return vesselType === 'power' ? gap.filter((m) => m !== SAILING) : gap;
}

/** Plain language for a mode the set is missing — never the raw mode id. */
const GAP_PHRASE: Readonly<Record<string, string>> = {
  Sailing: 'while sailing',
  Underway: 'under power',
};

/**
 * One missing mode, in the words the gap disclosure uses — `while sailing`,
 * `under power`. Exported because the READ surface has to say the same thing:
 * `alarmDisplay.alarmPausedNote` pauses a row whose stored set is missing the
 * mode the boat is in right now, and it must not invent a second vocabulary for
 * the same fact. A mode with no phrase degrades to `in <Mode>` rather than
 * being dropped.
 */
export function watchGapPhrase(mode: string): string {
  return GAP_PHRASE[mode] ?? `in ${mode}`;
}

/**
 * The one-line disclosure for a set that is missing part of the preset it reads
 * as, or `null` when there is nothing to disclose.
 *
 * It names the fix rather than only the problem, and the fix is derived: the
 * presets that WOULD write the missing mode, by their own labels. Re-picking one
 * writes the full set (that is what {@link watchPresetModes} returns), which is
 * what makes this note disappear.
 *
 * A declared power boat has no sailing gap to disclose ({@link watchPresetGap}),
 * so this goes quiet there — one fewer thing to read on a screen full of them.
 */
export function watchGapNote(
  modes: readonly string[],
  vesselType?: VesselType | null
): string | null {
  const gap = watchPresetGap(modes, vesselType);
  if (gap.length === 0) return null;
  const missing = gap.map((m) => watchGapPhrase(m)).join(' or ');
  const fixes = WATCH_PRESETS.filter((p) => gap.every((m) => p.modes.includes(m))).map(
    (p) => p.label
  );
  if (fixes.length === 0) return null;
  return `Doesn’t watch ${missing} yet — pick ${fixes.join(' or ')} again to include it.`;
}
